import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Wrench, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserOrganization } from "@/hooks/use-user-organization";

interface CustomWorkflowProps {
  device: any;
  deviceId: string;
  isUpdating: boolean;
  canEdit: boolean;
  organizationId?: string | null;
}

export const CustomWorkflow = ({ device, deviceId, isUpdating, canEdit, organizationId: organizationIdProp }: CustomWorkflowProps) => {
  const { data: userOrganizationId } = useUserOrganization();
  const organizationId = organizationIdProp ?? userOrganizationId;

  const { data: orgData } = useQuery({
    queryKey: ['organization-custom-workflow', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      const { data, error } = await supabase
        .from('organizations')
        .select('custom_workflow_id, custom_workflow_description')
        .eq('id', organizationId)
        .single();
      if (error) {
        console.error('Error fetching org custom workflow:', error);
        return null;
      }
      return data;
    },
    enabled: !!organizationId,
  });

  return (
    <Card className="border bg-muted/50">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-4">
          <Wrench className="h-4 w-4 text-brand-500" />
          <span className="text-sm font-semibold">Custom Workflow</span>
        </div>

        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            This workflow uses your organisation's custom configuration. To change these settings, visit the Organisation Profile page.
          </AlertDescription>
        </Alert>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Custom Workflow ID</Label>
            <Input
              value={orgData?.custom_workflow_id || ''}
              readOnly
              disabled
              className="h-9 bg-muted"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Custom Workflow Description</Label>
            <Input
              value={orgData?.custom_workflow_description || ''}
              readOnly
              disabled
              className="h-9 bg-muted"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
